<template>
  <div class="invite-container">
    <div class="invite-content">
      <Loader v-if="loading" color="#FF035A" size="50" />
      <div v-else-if="error" class="error-state" role="alert">
        <h2>Invitation unavailable</h2>
        <p>{{ errorMessage }}</p>
        <button v-if="wrongAccount" class="primary" @click="switchAccount">
          Sign in with invited account
        </button>
        <button
          class="primary"
          @click="$router.push({ name: isLoggedIn ? 'Catalog' : 'Login' })"
        >
          {{ isLoggedIn ? "Return to Catalog" : "Go to Login" }}
        </button>
      </div>
      <div v-else-if="!isLoggedIn" class="processing-state">
        <h2>You’re invited to collaborate</h2>
        <p>
          Sign in with the email address that received this invitation, or
          create an account.
        </p>
        <button
          class="primary"
          @click="$router.push({ name: 'Login', query: { invitation: token } })"
        >
          Sign in to accept
        </button>
        <button
          class="primary outline"
          @click="
            $router.push({ name: 'Signup', query: { invitation: token } })
          "
        >
          Create an account
        </button>
      </div>
      <div v-else class="processing-state">
        <h2>Processing Invitation...</h2>
        <p>Please wait while we set up your collaboration.</p>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from "@/components/Loader.vue";
import { normalizeEmail } from "@/utils/normalizeEmail";
import GET_INVITED_USER_FROM_TOKEN from "@/api/queries/GET_INVITED_USER_FROM_TOKEN.gql";
import SYNC_CONTACTS from "@/api/mutations/SYNC_CONTACTS.gql";

export default {
  name: "Invite",
  components: {
    Loader
  },
  data() {
    return {
      loading: true,
      wrongAccount: false,
      error: false,
      errorMessage: ""
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    isLoggedIn() {
      return !!this.$store.getters["account/getApolloToken"];
    },
    userEmail() {
      const user = this.$store.getters["account/getUser"];
      return user?.email;
    },
    artistId() {
      return this.$store.getters["account/getArtistId"];
    }
  },
  async mounted() {
    await this.handleInvitation();
  },
  methods: {
    async handleInvitation() {
      if (!this.token) {
        this.showError("No invitation token provided.");
        return;
      }

      // User is logged in, try to accept the invitation
      try {
        // First validate the token and get invitation details
        const tokenResult = await this.$apollo.query({
          query: GET_INVITED_USER_FROM_TOKEN,
          variables: { token: this.token },
          fetchPolicy: "network-only"
        });

        if (!tokenResult.data.get_invited_user_from_token.success) {
          this.showError("This invitation link is invalid or has expired.");
          return;
        }

        if (!this.isLoggedIn) {
          this.loading = false;
          return;
        }
        if (!this.artistId) {
          this.$router.replace({
            name: "Onboarding",
            query: { invitation: this.token }
          });
          return;
        }
        const invitedEmail = tokenResult.data.get_invited_user_from_token.email;

        // Check if the logged-in user's email matches the invited email
        if (
          invitedEmail &&
          normalizeEmail(this.userEmail) !== normalizeEmail(invitedEmail)
        ) {
          this.wrongAccount = true;
          this.showError(
            "This invitation is for a different account. Sign out and sign in with the email address that received the invitation, then reopen this link."
          );
          return;
        }

        // Accept the invitation using sync_collaborators
        const syncResult = await this.$apollo.mutate({
          mutation: SYNC_CONTACTS,
          variables: {
            invitation_token: this.token,
            email: normalizeEmail(this.userEmail),
            specific_artist_id: this.artistId,
            response: "accepted"
          }
        });

        if (syncResult.data.sync_collaborators.success) {
          // Successfully accepted - redirect to Catalog so the guest can review the contract
          this.$errorService.showInfo(
            "Invitation accepted! You are now connected.",
            "Success"
          );
          this.$router.replace({
            name: "Catalog",
            query: { fromInvite: "true" }
          });
        } else {
          this.showError(
            syncResult.data.sync_collaborators.message ||
              "Failed to accept the invitation. Please try again."
          );
        }
      } catch (e) {
        console.error("Error processing invitation:", e);
        this.showError(
          "An error occurred while processing the invitation. Please try again."
        );
      }
    },
    switchAccount() {
      this.$store.commit("account/logout");
      window.location.href = "/?invitation=" + encodeURIComponent(this.token);
    },
    showError(message) {
      this.loading = false;
      this.error = true;
      this.errorMessage = message;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/_mixins.scss";

.invite-container {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: $black;
}

.invite-content {
  text-align: center;
  padding: 40px;
  background: $text;
  border-radius: 15px;
  max-width: 500px;
  width: 90%;

  h2 {
    color: $white;
    margin-bottom: 16px;
  }

  p {
    color: #eeeeee;
    margin-bottom: 24px;
  }

  button {
    min-width: 150px;
    margin-top: 12px;
  }
}

.error-state,
.processing-state {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
